import { WITNESS_LINE_GAP } from "building-types/constants/dimensionConstants";
import type { Dimension, DimensionRowOutput } from "building-types/dimensions/dimensionTypes";
import { DimensionRowLevel } from "building-types/dimensions/dimensionTypesEnum";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import { BinaryTreeIterator, BinaryTreeNode } from "common/data/binaryTree";
import { getOrientedDimLine } from "doc/dimensions";
import { arrayEdgeDataUnion, edgeDataCopy, polyLine2EdgeDataCopy } from "doc/provenance/halfEdgeDataTools";
import { dimensionRowOutputNew } from "doc/utils/dimensionTools";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { ClosestPointToPolyLine2 } from "geom/closestPoint";
import { closestParamLine2, closestPointPolyLine2 } from "geom/closestPoint";
import { ANGLE_EPSILON, DIST_EPSILON, HALF_PI, TWO_PI, WindingDir } from "geom/core";
import { line2IsAxisAligned, line2IsCoincident, line2Normal } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2IsAxisAlignedEdge,
  polyLine2IsClosed,
  polyLine2Length,
  polyLine2ReverseCopy,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { type PolyLine2 } from "geom/polyLine2Types";
import { rect2ByAABB2 } from "geom/rect2";
import { vec2AngleBetween, vec2ArePerp, vec2Dist, vec2Dot, vec2Rotate } from "geom/vec2";
import { DIM_FONT_SIZE } from "pdf/planFonts";

interface DimensionTreeInputStyle {
  readonly witnessLineGap?: number;
}

export type DimensionRowInput =
  | DimensionTreeInputPolylines
  | DimensionTreeObstaclePolylines
  | DimensionTreeBoundingPolylines;

interface DimensionTreeInputPolylines extends DimensionTreeInputStyle {
  readonly pls: PolyLine2[];
  readonly halfEdges?: EdgeData[][][];
  readonly level: DimensionRowLevel.ROW_2 | DimensionRowLevel.ROW_3 | DimensionRowLevel.ROW_4;
  readonly maxOffset?: number;
  readonly projectOnAxis?: boolean;
  readonly isNonObstructing?: boolean;
}

interface DimensionTreeObstaclePolylines {
  readonly pls: PolyLine2[];
  readonly level: DimensionRowLevel.OBSTACLE;
}

interface DimensionTreeBoundingPolylines {
  readonly pls: PolyLine2[];
  readonly level: DimensionRowLevel.BOUNDS;
}

enum DimensionCondition {
  EXTERIOR = "EXTERIOR", // not obstructions
  // INTERIOR = "INTERIOR", // not in use
  INSET = "INSET", // segment has an opposing edge obstacle that is not coincident
  EDGE = "EDGE", // segment has an opposing edge obstacle that is coincident
}

interface DimensionTreeCoordinateSystem {
  readonly normalX: number;
  readonly normalY: number;
  readonly normalT: number;
  readonly tangentX: number;
  readonly tangentY: number;
  readonly tangentT: number;
}

interface DimensionTreePoint extends ClosestPointToPolyLine2 {
  readonly parentId: number;
  readonly level: number;
  readonly segmentDist?: number;
  readonly cs: DimensionTreeCoordinateSystem;
  readonly leftHalfEdges: EdgeData[];
  readonly rightHalfEdges: EdgeData[];
  readonly resultantLevel: DimensionRowLevel; // the resulting level of the dimension, rather than the input level
}

interface DimensionTreeSegmentCondition {
  readonly condition: DimensionCondition;
  readonly obstructionDistance: number;
  readonly obstruction?: DimensionTreeSegment;
  readonly mirror?: DimensionTreeSegment;
  readonly mirrorAngle?: number;
}

interface DimensionTreeSegment {
  readonly parentId: number;
  readonly line: PolyLine2;
  readonly level: DimensionRowLevel;
  readonly sptCS: DimensionTreeCoordinateSystem;
  readonly sptDist?: number;
  readonly eptCS: DimensionTreeCoordinateSystem;
  readonly eptDist?: number;
  readonly tMax?: number; // allowable offset for this segment
  readonly isNonObstructing?: boolean;
  halfEdges: EdgeData[];
}

interface DimensionTreeSegmentGroup {
  readonly angle: number;
  readonly segments: DimensionTreeSegment[];
  readonly conditions: DimensionTreeSegmentCondition[];
}

type DimensionTree = {
  [angle: number]: BinaryTreeNode<DimensionTreeSegment>;
};

enum DimensionRowStyle {
  THREE_ROW,
  FOUR_ROW,
}

interface DimensionRowConfiguration {
  readonly baseLineOffset: number;
  readonly baseLineOffsetMultiplier: number;
  readonly rowOffset: number;
  readonly rowCount: 3 | 4;
}

const dimensionRowConfigurations: { [key in DimensionRowStyle]: DimensionRowConfiguration } = {
  [DimensionRowStyle.THREE_ROW]: {
    baseLineOffset: 22.5, // 1.25 * rowOffset - legacy homes have baseLineOffsetMultiplier = 0.8
    baseLineOffsetMultiplier: 1,
    rowOffset: 18,
    rowCount: 3,
  },
  [DimensionRowStyle.FOUR_ROW]: {
    baseLineOffset: 16.875, // 1.25 * rowOffset - legacy homes have baselineOffsetMultiplier = 0.8
    baseLineOffsetMultiplier: 1,
    rowOffset: 13.5,
    rowCount: 4,
  },
};

/**
 * DimensionRow offsets are sorted from largest to smallest, so that:
 * - rows[0] = Bounds
 * - rows[1] = Max offset for dimension collision
 * - rows[2+] = Smaller row offsets (currently only 3 | 4)
 */
function makeDimensionRowOffsets(config: DimensionRowConfiguration) {
  const { rowOffset, rowCount, baseLineOffsetMultiplier, baseLineOffset } = config;
  const rows = new Array<number>(rowCount);
  const rowBaseOffset = baseLineOffset * baseLineOffsetMultiplier;
  for (let i = 1; i <= rowCount; i++) {
    rows[rowCount - i] = rowOffset * (i - 1) + rowBaseOffset;
  }
  return rows;
}

/**
 * This determines which input DimensionRowLevel and resultant DimensionRowLevel pairings will propagate
 * half edges thus passing them through the dimension tree.
 */
export type DimensionTreeHalfEdgePropagationConfig = {
  [key in DimensionRowLevel]?: { [key in DimensionRowLevel]?: boolean };
};

export function explicitPropagateHalfEdges(
  config: DimensionTreeHalfEdgePropagationConfig | undefined,
  from: DimensionRowLevel,
  to: DimensionRowLevel,
  defaultValue: boolean | undefined,
): boolean | undefined {
  if (!config) {
    return defaultValue;
  }
  const fromConfig = config[from];
  if (!fromConfig) {
    return defaultValue;
  }
  return fromConfig[to];
}

interface MakeExteriorDimensionRowOptions {
  output: DimensionRowOutput[];
  input: DimensionRowInput[];
  baseLineOffsetMultiplier?: number;
  mergeRows3And4?: boolean;
  halfEdgePropagationConfig?: DimensionTreeHalfEdgePropagationConfig;
}

export function makeExteriorDimensionRows({
  output,
  input,
  baseLineOffsetMultiplier = 1,
  mergeRows3And4 = true,
  halfEdgePropagationConfig,
}: MakeExteriorDimensionRowOptions) {
  const treeBuilder = new DimensionTreeBuilder();

  const allPls = Object.values(DimensionRowLevel).reduce(
    (v, l) => {
      v[l] = [];
      return v;
    },
    {} as { [K in DimensionRowLevel]: PolyLine2[] },
  );
  for (const inputPls of input) {
    const { level, pls } = inputPls;
    allPls[level].push(...pls);
    if (level === DimensionRowLevel.BOUNDS) {
      /**
       * Bounds are not added directly but computed below into a new
       * set of segments
       */
      continue;
    }
    treeBuilder.addRowInput(inputPls);
  }

  const treeBounds = makeDimensionTreeBounds(allPls);
  const treeBoundSegments = makeDimensionTreeBoundSegments(treeBounds);

  treeBuilder.addRowInput({ pls: treeBoundSegments, level: DimensionRowLevel.BOUNDS });

  const rowStyle = mergeRows3And4 ? DimensionRowStyle.THREE_ROW : DimensionRowStyle.FOUR_ROW;
  const rowConfig = {
    ...dimensionRowConfigurations[rowStyle],
    baseLineOffsetMultiplier,
  };

  treeBuilder.buildDimensions({
    dimensions: output,
    rowConfig,
    halfEdgePropagationConfig,
  });
}

interface MakeInteriorDimensionRowOptions {
  dimensions: DimensionRowOutput[];
  perimeters: PolyLine2[];
  perimeterHalfEdges: EdgeData[][][];
  interiorEdges?: PolyLine2[];
  interiorEdgeHalfEdges?: EdgeData[][][];
  obstacles?: PolyLine2[];
  baseLineOffsetMultiplier?: number;
}

export function makeInteriorDimensionRows({
  dimensions,
  perimeters,
  interiorEdges = [],
  obstacles = [],
  baseLineOffsetMultiplier = 1,
  perimeterHalfEdges,
  interiorEdgeHalfEdges,
}: MakeInteriorDimensionRowOptions) {
  const treeBuilder = new DimensionTreeBuilder(true);

  // projecting interior dimensions to the axis produces a lot of clutter, so for now is disabled
  treeBuilder.addRowInput({
    pls: perimeters,
    level: DimensionRowLevel.ROW_2,
    projectOnAxis: false,
    halfEdges: perimeterHalfEdges,
  });

  treeBuilder.addRowInput({ pls: interiorEdges, level: DimensionRowLevel.ROW_4, halfEdges: interiorEdgeHalfEdges });
  treeBuilder.addRowInput({ pls: obstacles, level: DimensionRowLevel.OBSTACLE });

  const rowConfig = {
    ...dimensionRowConfigurations[DimensionRowStyle.THREE_ROW],
    baseLineOffsetMultiplier,
  };

  treeBuilder.buildDimensions({
    dimensions,
    rowConfig,
    witnessLineGap: 0,
    halfEdgePropagationConfig: { [DimensionRowLevel.ROW_2]: { [DimensionRowLevel.ROW_3]: true } },
  });
}

const MIN_AXIS_LINE_LEN = 24; // minimum edge length for axis projection

class DimensionTreeBuilder {
  private tree: DimensionTree = {};
  private styles: DimensionTreeInputStyle[] = [];

  constructor(
    private interior = false,
    private allowMirroredEdges = false,
    private allowJoinAllExteriorCollinearEdges = false,
  ) {
    this.inputCount = 0;
  }

  private inputCount: number;

  addRowInput(rowInput: DimensionRowInput) {
    const id = this.inputCount++;
    if (rowInput.level === DimensionRowLevel.BOUNDS || rowInput.level === DimensionRowLevel.OBSTACLE) {
      const { pls, level } = rowInput;
      pls.forEach((pl) =>
        this.addPolyLine2({
          parentId: id,
          pl,
          level,
        }),
      );
    } else {
      const { pls, level, maxOffset, projectOnAxis, isNonObstructing, witnessLineGap, halfEdges } = rowInput;
      pls.forEach((pl, i) =>
        this.addPolyLine2({
          parentId: id,
          pl,
          edgeData: halfEdges ? halfEdges[i] : undefined,
          level,
          maxOffset,
          projectOnAxis,
          isNonObstructing,
        }),
      );
      this.styles[id] = {
        witnessLineGap,
      };
    }
  }

  private addPolyLine2({
    parentId,
    pl,
    edgeData,
    level,
    maxOffset,
    projectOnAxis = false,
    isNonObstructing = false,
    sptDist,
    eptDist,
  }: {
    parentId: number;
    pl: PolyLine2;
    edgeData?: EdgeData[][];
    level: DimensionRowLevel;
    maxOffset?: number;
    projectOnAxis?: boolean;
    sptDist?: number;
    eptDist?: number;
    isNonObstructing?: boolean;
  }) {
    if (polyLine2IsClosed(pl)) {
      if (this.interior) {
        if (level === DimensionRowLevel.OBSTACLE) {
          pl = polyLine2SetCCW(polyLine2Copy(pl));
        } else {
          if (edgeData && polyLine2WindingDir(pl) !== WindingDir.CW) {
            edgeData = polyLine2EdgeDataCopy(edgeData).reverse();
          }
          pl = polyLine2SetCW(polyLine2Copy(pl));
        }
      } else {
        if (level === DimensionRowLevel.OBSTACLE) {
          pl = polyLine2SetCW(polyLine2Copy(pl));
        } else {
          if (edgeData && polyLine2WindingDir(pl) !== WindingDir.CCW) {
            edgeData = polyLine2EdgeDataCopy(edgeData).reverse();
          }
          pl = polyLine2SetCCW(polyLine2Copy(pl));
        }
      }
    }

    for (let i = 0; i < pl.length - 3; i += 2) {
      const ax = pl[i];
      const ay = pl[i + 1];
      const bx = pl[i + 2];
      const by = pl[i + 3];

      const segmentData = edgeData ? edgeData[i / 2] : [];

      const line = polyLine2ByTwoPoints(ax, ay, bx, by);
      const lineLen = vec2Dist(ax, ay, bx, by);

      if (lineLen <= DIST_EPSILON) {
        continue;
      }

      const [normalX, normalY] = line2Normal(ax, ay, bx, by, false);
      let angle = line2Angle(ax, ay, bx, by);

      // if a similar angle exists on the tree, use that instead
      angle = findClosestAngle(angle, this.tree) ?? angle;

      const sptCS = vec2ToCoordinateSystem(ax, ay, angle);
      const eptCS = vec2ToCoordinateSystem(bx, by, angle);

      if (projectOnAxis && lineLen >= MIN_AXIS_LINE_LEN && !line2IsAxisAligned(0, 0, normalX, normalY)) {
        for (const axis of projectLineToAxis(line)) {
          this.addPolyLine2({
            parentId,
            pl: axis.segment,
            edgeData: polyLine2EdgeDataCopy([segmentData]),
            level,
            projectOnAxis: false,
            isNonObstructing: true,
            sptDist: axis.sptDist,
            eptDist: axis.eptDist,
          });
        }
      }

      if (!this.tree[angle]) {
        this.tree[angle] = new BinaryTreeNode<DimensionTreeSegment>(segmentNormalT, undefined, true);
      }

      this.tree[angle].insert({
        parentId,
        line,
        halfEdges: edgeDataCopy(segmentData),
        level,
        sptCS,
        sptDist,
        eptCS,
        eptDist,
        tMax: maxOffset,
        isNonObstructing,
      });
    }
  }

  /**
   *
   * @param line
   * @param level
   * @returns
   */
  private line2ToDimensionTreeSegment(line: PolyLine2, level: DimensionRowLevel, parentId: number) {
    const [ax, ay, bx, by] = line;
    let angle = line2Angle(ax, ay, bx, by);
    // if a similar angle exists on the tree, use that instead
    angle = findClosestAngle(angle, this.tree) ?? angle;

    const sptCS = vec2ToCoordinateSystem(ax, ay, angle);
    const eptCS = vec2ToCoordinateSystem(bx, by, angle);

    const segment: DimensionTreeSegment = {
      parentId,
      line,
      level,
      sptCS,
      eptCS,
      halfEdges: [],
    };

    return {
      angle,
      segment,
    };
  }

  /**
   *
   * Creates dimension rows from PolyLine2s added to this dimensionBuilder.
   *
   */
  buildDimensions({
    dimensions,
    witnessLineGap = WITNESS_LINE_GAP,
    textHeight = DIM_FONT_SIZE,
    extendLeaders = true,
    rowConfig,
    baseLineFlipAllowed = false,
    halfEdgePropagationConfig,
  }: {
    dimensions: DimensionRowOutput[];
    textHeight?: number;
    witnessLineGap?: number;
    extendLeaders?: boolean;
    rowConfig: DimensionRowConfiguration;
    baseLineFlipAllowed?: boolean;
    halfEdgePropagationConfig?: DimensionTreeHalfEdgePropagationConfig;
  }) {
    const offsets = makeDimensionRowOffsets(rowConfig);
    const maxDistance = offsets[1];
    const maxLevelOffset = maxDistance + witnessLineGap;

    // utility methods for creating dimensions

    const addDimension = ({
      startPoint,
      endPoint,
      offset,
    }: {
      startPoint: DimensionTreePoint;
      endPoint: DimensionTreePoint;
      offset: number;
    }) => {
      const pl = polyLine2ByTwoPoints(startPoint.x, startPoint.y, endPoint.x, endPoint.y);
      let startDist = startPoint.dist + (startPoint.segmentDist || 0);
      let eptDist = endPoint.dist + (endPoint.segmentDist || 0);
      const baseLineOffset = offset;

      const lineGap = witnessLineGap;
      const sGap = this.styles[startPoint.parentId]?.witnessLineGap;
      if (sGap !== undefined) {
        const startLineGap = sGap - witnessLineGap;
        startDist -= startLineGap;
      }

      const eGap = this.styles[endPoint.parentId]?.witnessLineGap;
      if (eGap !== undefined) {
        const endLineGap = eGap - witnessLineGap;
        eptDist -= endLineGap;
      }

      dimensions.push(
        dimensionRowOutputNew({
          pl,
          baseLineOffset,
          halfEdges: arrayEdgeDataUnion(startPoint.rightHalfEdges, endPoint.leftHalfEdges),
          rowLevel: startPoint.resultantLevel ?? endPoint.resultantLevel, //both levels should be the same and populated and this fallback should not be necessary
          startExt: extendLeaders ? startDist : undefined,
          endExt: extendLeaders ? eptDist : undefined,
          witnessLineGap: lineGap,
        }),
      );
    };

    const addDimensionString = ({
      node,
      offset,
      parents = [],
      flip = false,
    }: {
      node: BinaryTreeNode<DimensionTreePoint>;
      offset: number;
      parents?: BinaryTreeNode<DimensionTreePoint>[];
      flip?: boolean;
    }) => {
      let startPoint: DimensionTreePoint;
      const addPointToDimString = (endPoint: DimensionTreePoint) => {
        if (startPoint) {
          // filter duplicate dims
          for (const node of parents) {
            if (node.has(startPoint) && node.has(endPoint)) {
              startPoint = endPoint;
              return;
            }
          }
          addDimension({ startPoint, endPoint, offset });
        }
        startPoint = endPoint;
      };
      node.forEach(flip ? BinaryTreeIterator.DECREASING : BinaryTreeIterator.INCREASING, addPointToDimString);
    };

    // iterate all angles in the tree and create dimensions for that angle

    for (const angle in this.tree) {
      // group segments into allowable dimension strings by visibility
      const angleGroups = this.groupSegmentsAtAngle(+angle);
      const angleNodes: BinaryTreeNode<DimensionTreePoint>[] = [];

      if (!angleGroups.length) {
        continue;
      }

      // points added to this node will generate the bounds for this angle
      const bounds = new BinaryTreeNode<DimensionTreePoint>(pointTangentT, undefined, false);
      const boundsLine = angleGroups[0].segments.find((s) => s.level === DimensionRowLevel.BOUNDS)?.line;

      for (const group of angleGroups) {
        // the first condition in this group belongs to the closest segment to the dimension line
        const condition = group.conditions[0];

        let flip = false;
        let scale = 1;
        let offset = condition.obstructionDistance;

        // if the condition is not exterior and the obstructing segment will be dimensioned,
        // we need to account for facing dimensions
        if (condition.obstruction && condition.obstruction.level !== DimensionRowLevel.OBSTACLE) {
          offset /= 2;
          offset -= textHeight / 2;
          offset = Math.max(0.1, offset);
        }

        // this dimension will not fit with the desired offset;
        if (maxLevelOffset >= offset) {
          if (baseLineFlipAllowed) {
            flip = true;
          } else {
            scale = offset / maxLevelOffset;
          }
        }

        const index = flip ? group.segments.length - 1 : 0;
        const line = polyLine2ReverseCopy(group.segments[index].line);

        const row2 = new BinaryTreeNode<DimensionTreePoint>(pointTangentT);
        const row3 = new BinaryTreeNode<DimensionTreePoint>(pointTangentT);
        const row4 = new BinaryTreeNode<DimensionTreePoint>(pointTangentT);

        // insert into the dimension nodes
        for (const segment of group.segments) {
          switch (segment.level) {
            case DimensionRowLevel.BOUNDS: {
              if (boundsLine) {
                nodeInsert(segment, boundsLine, bounds, DimensionRowLevel.BOUNDS);
              }
              nodeInsert(segment, line, row2, DimensionRowLevel.ROW_2);
              nodeInsert(segment, line, row3, DimensionRowLevel.ROW_3);
              nodeInsert(segment, line, row4, DimensionRowLevel.ROW_4);
              break;
            }
            case DimensionRowLevel.ROW_2: {
              if (boundsLine) {
                nodeInsert(segment, boundsLine, bounds);
              }
              nodeInsert(segment, line, row2, DimensionRowLevel.ROW_2);
              nodeInsert(
                segment,
                line,
                row3,
                DimensionRowLevel.ROW_3,
                explicitPropagateHalfEdges(
                  halfEdgePropagationConfig,
                  DimensionRowLevel.ROW_2,
                  DimensionRowLevel.ROW_3,
                  false,
                ),
              );
              nodeInsert(segment, line, row4, DimensionRowLevel.ROW_4, false);
              break;
            }
            case DimensionRowLevel.ROW_3:
              nodeInsert(
                segment,
                line,
                row3,
                DimensionRowLevel.ROW_3,
                explicitPropagateHalfEdges(
                  halfEdgePropagationConfig,
                  DimensionRowLevel.ROW_3,
                  DimensionRowLevel.ROW_3,
                  undefined,
                ) ?? rowConfig.rowCount === 4,
              );
              nodeInsert(segment, line, row4, DimensionRowLevel.ROW_4, false);
              break;
            case DimensionRowLevel.ROW_4: {
              if (rowConfig.rowCount === 3) {
                nodeInsert(segment, line, row3, DimensionRowLevel.ROW_3);
              }
              nodeInsert(segment, line, row4, DimensionRowLevel.ROW_4);
              break;
            }
            case DimensionRowLevel.OBSTACLE: {
              break;
            }
          }
        }

        if (!this.interior) {
          // if this is an exterior condition with count == 2,
          // it will be identical to the bounds at this angle
          if (condition.condition !== DimensionCondition.EXTERIOR || row2.count !== 2) {
            addDimensionString({
              node: row2,
              offset: offsets[1] * scale,
              flip,
              parents: angleNodes,
            });
          }

          angleNodes.push(row2);

          addDimensionString({
            node: row3,
            offset: offsets[2] * scale,
            flip,
            parents: angleNodes,
          });

          angleNodes.push(row3);

          if (rowConfig.rowCount === 4) {
            addDimensionString({
              node: row4,
              offset: offsets[3] * scale,
              flip,
              parents: angleNodes,
            });

            angleNodes.push(row3);
          }
        } else {
          // flat heirarchy for interior dimensions
          addDimensionString({
            node: row3,
            offset: offsets[2] * scale,
            flip,
          });

          angleNodes.push(row3);
        }
      }

      // make the bounds at this angle
      if (bounds.count > 1) {
        const boundsFirst = bounds.first();
        const boundsLast = bounds.last();

        if (!!boundsFirst && !!boundsLast) {
          addDimension({
            startPoint: boundsFirst,
            endPoint: boundsLast,
            offset: offsets[0],
          });
        }
      }
    }
  }

  /**
   * This method iterates through all segments at a same angle and detects the nearest obstruction
   * to each segment. It then groups those segments based on the distance to their nearest obstruction
   * such that they can be dimensioned without overlaps.
   *
   * @param angle angle in the tree to group
   */
  private groupSegmentsAtAngle(angle: number) {
    const groups: DimensionTreeSegmentGroup[] = [];
    const grouped: DimensionTreeSegment[] = [];

    // iterate segments from bounding edge backwards
    this.tree[angle].forEach(BinaryTreeIterator.DECREASING, (segment) => {
      // dont dimension obstacles
      if (segment.level === DimensionRowLevel.OBSTACLE) {
        return;
      }

      // filter out duplicate segments
      for (const otherSegment of grouped) {
        if (segmentsAreEqual(segment, otherSegment)) {
          if (segment?.halfEdges) {
            if (otherSegment?.halfEdges) {
              otherSegment.halfEdges = arrayEdgeDataUnion(otherSegment.halfEdges, segment.halfEdges);
            }
          }
          return;
        }
      }

      // get the dimension condition for this segment with the current obstruction filter
      const sc = this.detectSegmentNormalCondition(angle, segment);

      // if there is a mirrored edge, and that edge will also be dimensioned, we dont want to draw both
      if (!this.allowMirroredEdges && sc.mirrorAngle) {
        if (sc.mirror && sc.mirrorAngle > angle && sc.mirror.level !== DimensionRowLevel.OBSTACLE) {
          return;
        }
      }

      // check that this segment condition should be included on the dimensions
      if (this.interior) {
        if (sc.condition === DimensionCondition.EXTERIOR) {
          return;
        }
      } else {
        if (sc.condition === DimensionCondition.EDGE) {
          return;
        }
      }

      // if this segment has a max offset, make sure that is met
      const segmentT = segment.sptCS.normalT;
      let segmentDist = Math.min(segment.tMax ?? sc.obstructionDistance, sc.obstructionDistance);

      let found = false;
      let candidates = groups.slice();

      // if this is interior dimension, make sure that it won't have leaders longer than
      // the segment that created it
      if (this.interior && segment.level > DimensionRowLevel.ROW_2) {
        segmentDist = Math.min(segmentDist, polyLine2Length(segment.line));
      }

      // if this is an interior dimension the only allowable dimension to join is the
      // last one that was created
      if (this.interior && candidates.length) {
        candidates = [candidates[candidates.length - 1]];
      }

      // if the last candidate is on the same line as the test segment
      // that is the only option
      if (candidates.length) {
        const lastCandidate = candidates[candidates.length - 1];
        const lastCandidateT = lastCandidate.segments[0].sptCS.normalT;
        if (Math.abs(lastCandidateT - segmentT) < DIST_EPSILON) {
          candidates = [lastCandidate];
        }
      }

      // since we are iterating from front to back, any possible matches will already be in the existing groups
      for (const group of candidates) {
        const groupT = group.segments[0].sptCS.normalT;
        const groupDist = group.conditions[0].obstructionDistance;

        // this segment can be joined the group if the first item in the group is visible
        if (segmentT + segmentDist + DIST_EPSILON <= groupT) {
          continue;
        }

        // if this is an exterior dimension condition, we know they can be joined
        if (sc.condition === DimensionCondition.EXTERIOR) {
          if (this.allowJoinAllExteriorCollinearEdges || polyLine2IsAxisAlignedEdge(segment.line, 0)) {
            group.segments.push(segment);
            group.conditions.push(sc);
            found = true;
            break;
          }
        }

        // interior joining can happen if
        // the segment visibility is at least as far as the group it is joining
        if (this.interior && segmentT + sc.obstructionDistance + DIST_EPSILON < groupT + groupDist) {
          continue;
        }

        if (this.segmentCanJoinGroup(group, segment)) {
          group.segments.push(segment);
          group.conditions.push(sc);
          found = true;
          break;
        }
      }

      // make a new group if none is found
      if (!found) {
        groups.push({
          angle,
          segments: [segment],
          conditions: [sc],
        });
      }

      grouped.push(segment);
    });

    return groups;
  }

  /**
   * Checks to see if the new group will create intersections with existing geometry
   * @param segment
   * @param segmentDist
   * @returns
   */
  private segmentCanJoinGroup(group: DimensionTreeSegmentGroup, segment: DimensionTreeSegment) {
    if (segmentOverlapsGroupBounds(group, segment)) {
      return true;
    }

    if (!this.interior) {
      return false;
    }

    // This is a bit hacky, and should be revisited.
    // it allows any interior segment that is above row 2 to be joined regardless of intersections
    if (segment.level > DimensionRowLevel.ROW_2) {
      return true;
    }

    const groupLine = group.segments[0].line;
    const groupNode = new BinaryTreeNode<DimensionTreePoint>(pointTangentT, undefined, false);
    nodeInsert(segment, groupLine, groupNode);
    for (const groupSegment of group.segments) {
      nodeInsert(groupSegment, groupLine, groupNode);
    }

    // Again, a bit hacky, and should be revisited.
    // we are only interested in intersections with bounding polylines and obstacles
    const intersectionPredicate = (s: DimensionTreeSegment) => s.level <= DimensionRowLevel.ROW_2;

    const start = groupNode.first();
    const end = groupNode.last();
    if (!start || !end) {
      return false;
    }

    const pl = polyLine2ByTwoPoints(start.x, start.x, end.x, end.y);
    const plOffset = offsetPolyLine2(polyLine2Copy(pl), segment.tMax ?? 1);
    const plLen = polyLine2Length(pl);

    let line = polyLine2ByTwoPoints(pl[0], pl[1], plOffset[0], plOffset[1]);
    let ls = this.line2ToDimensionTreeSegment(line, DimensionRowLevel.ROW_4, segment.parentId);
    let condition = this.detectSegmentNormalCondition(ls.angle, ls.segment, undefined, intersectionPredicate);
    if (condition.obstructionDistance < plLen) {
      return false;
    }

    line = polyLine2ByTwoPoints(pl[2], pl[3], plOffset[2], plOffset[2]);
    ls = this.line2ToDimensionTreeSegment(line, DimensionRowLevel.ROW_4, segment.parentId);
    condition = this.detectSegmentNormalCondition(ls.angle, ls.segment, undefined, intersectionPredicate);
    if (condition.obstructionDistance < plLen) {
      return false;
    }

    return true;
  }

  /**
   * This method finds the nearest obstructing (facing && overlapping) segment to a test segment.
   * It returns the obstructing segment, the distance to that obstruction,
   * and the type of obstruction condition that was found (or EXTERIOR if no obstruction exists).
   *
   * @param angle angle of the segment within the tree
   * @param segment segment to find the condition for
   * @returns
   */
  private detectSegmentNormalCondition(
    angle: number,
    segment: DimensionTreeSegment,
    keepEdgeObstructionDistance = false,
    segmentObstructionPredicate?: (s: DimensionTreeSegment) => boolean,
  ): DimensionTreeSegmentCondition {
    const line = segment.line;

    let condition = DimensionCondition.EXTERIOR;

    let obstructionDistance = Number.MAX_VALUE;
    let obstruction: DimensionTreeSegment | undefined = undefined;
    let mirrorAngle: number | undefined = undefined;
    let mirror: DimensionTreeSegment | undefined = undefined;

    // iterate other angles that face this group
    for (const otherAngle in this.tree) {
      // get angles in front of this angle
      // we only need to check facing groups and edges in the groups that are in front of this edge
      if (!anglesAreFacing(angle, +otherAngle)) {
        continue;
      }

      // project this segment to the group at the other angle
      const sptCS = vec2ToCoordinateSystem(line[0], line[1], +otherAngle);
      const eptCS = vec2ToCoordinateSystem(line[2], line[3], +otherAngle);

      // furthest projected t from the dim line at the other angle
      const t = Math.min(sptCS.normalT, eptCS.normalT) + DIST_EPSILON;

      // get segments in front of this segment that are allowable obstructions
      const otherSegments = this.tree[otherAngle].retrieve(
        (otherSegment) =>
          !otherSegment.isNonObstructing && (!segmentObstructionPredicate || segmentObstructionPredicate(otherSegment)),
        undefined,
        undefined,
        undefined,
        t,
      );

      // iterate other segments in increasing distance from this segment
      for (let i = otherSegments.length - 1; i >= 0; i--) {
        const otherSegment = otherSegments[i];
        const otherLine = otherSegment.line;

        let cd: number | undefined = undefined;
        let co: DimensionTreeSegment | undefined = undefined;

        // distance from other segment to this segment
        const c1 = closestPointPolyLine2(line, otherLine[0], otherLine[1]);
        const c2 = closestPointPolyLine2(line, otherLine[2], otherLine[3]);

        // check if the other segment overlaps this one
        if (Math.abs(c1.t - c2.t) > DIST_EPSILON) {
          const d1 = closestPointPolyLine2(otherLine, line[0], line[1]);
          const d2 = closestPointPolyLine2(otherLine, line[2], line[3]);

          cd = Math.min(d1.dist, d2.dist);
          co = otherSegment;
        }

        // in this case we need to flag that this is an edge condition, but keep checking
        // the other segments to extract the true obstruction distance
        if (cd !== undefined && cd < DIST_EPSILON) {
          condition = DimensionCondition.EDGE;

          // check if the obstruction is the reverse of this polyline
          if (Math.abs(c1.t - 1) < DIST_EPSILON && Math.abs(c2.t) < DIST_EPSILON) {
            mirror = co;
            mirrorAngle = +otherAngle;
          }

          // return here if this is flagged. obstruction distance will be < DIST_EPSILON
          if (keepEdgeObstructionDistance) {
            obstructionDistance = cd;
            obstruction = co;
            break;
          }
        } else if (cd !== undefined && cd < obstructionDistance) {
          // no need to go further - this is the closest facing overlap at the other angle
          obstructionDistance = cd;
          obstruction = co;

          if (condition !== DimensionCondition.EDGE) {
            condition = DimensionCondition.INSET;
          }

          break;
        }
      }
    }

    return {
      condition,
      obstructionDistance,
      obstruction,
      mirror,
      mirrorAngle,
    };
  }
}

// insert a segment into a dimension string node by evaluating its closest point to a reference line
function nodeInsert(
  dimSegment: DimensionTreeSegment,
  dimLine: PolyLine2,
  dimNode: BinaryTreeNode<DimensionTreePoint>,
  resultantLevel?: DimensionRowLevel, // used for dimension provenance
  transmitSegmentHalfEdges = true,
) {
  const spt = closestPointPolyLine2(dimLine, dimSegment.line[0], dimSegment.line[1], false);
  const ept = closestPointPolyLine2(dimLine, dimSegment.line[2], dimSegment.line[3], false);

  // predicate for which points to keep if two same points are found
  const pointOverridePredicate = (currPt: DimensionTreePoint, newPt: DimensionTreePoint) => {
    if (currPt.level === DimensionRowLevel.BOUNDS) {
      // bounds should always be overridden by another level
      return true;
    } else if (newPt.level === DimensionRowLevel.BOUNDS) {
      // bounds should always be overridden by another level
      return false;
    } else {
      // if the distance is the same, override with the lower level (higher priority) point
      if (Math.abs(currPt.cs.normalT - newPt.cs.normalT) < DIST_EPSILON) {
        return currPt.level > newPt.level;
      }
      // override with the closest point
      return currPt.cs.normalT < newPt.cs.normalT;
    }
  };

  const combineHalfEdgeData = (
    curPt: DimensionTreePoint,
    newPt: DimensionTreePoint,
    overridePredicateValue: boolean,
  ) => {
    if (overridePredicateValue) {
      return {
        ...newPt,
        leftHalfEdges: arrayEdgeDataUnion(curPt.leftHalfEdges, newPt.leftHalfEdges),
        rightHalfEdges: arrayEdgeDataUnion(curPt.rightHalfEdges, newPt.rightHalfEdges),
      };
    } else {
      return {
        ...curPt,
        leftHalfEdges: arrayEdgeDataUnion(curPt.leftHalfEdges, newPt.leftHalfEdges),
        rightHalfEdges: arrayEdgeDataUnion(curPt.rightHalfEdges, newPt.rightHalfEdges),
      };
    }
  };

  const halfEdgeData =
    !transmitSegmentHalfEdges || dimSegment?.halfEdges === undefined ? [] : [...dimSegment.halfEdges];

  dimNode.insert(
    {
      ...spt,
      level: dimSegment.level,
      segmentDist: dimSegment.sptDist,
      cs: dimSegment.sptCS,
      parentId: dimSegment.parentId,
      leftHalfEdges: edgeDataCopy(halfEdgeData),
      rightHalfEdges: [],
      resultantLevel: resultantLevel ?? DimensionRowLevel.BOUNDS,
    },
    pointOverridePredicate,
    combineHalfEdgeData,
  );
  dimNode.insert(
    {
      ...ept,
      level: dimSegment.level,
      leftHalfEdges: [],
      rightHalfEdges: edgeDataCopy(halfEdgeData),
      segmentDist: dimSegment.eptDist,
      cs: dimSegment.eptCS,
      parentId: dimSegment.parentId,
      resultantLevel: resultantLevel ?? DimensionRowLevel.BOUNDS,
    },
    pointOverridePredicate,
    combineHalfEdgeData,
  );
}

// points sorted along dimension axis
function pointTangentT(pt: DimensionTreePoint) {
  return pt.cs.tangentT;
}

// segments sorted along normal axis
function segmentNormalT(seg: DimensionTreeSegment) {
  return seg.sptCS.normalT;
}

// check that two segments at the same angle are equal
function segmentsAreEqual(s1: DimensionTreeSegment, s2: DimensionTreeSegment) {
  if (s1.level !== s2.level) {
    return false;
  }

  if (
    Math.abs(s1.sptCS.tangentT - s2.sptCS.tangentT) < DIST_EPSILON &&
    Math.abs(s1.eptCS.tangentT - s2.eptCS.tangentT) < DIST_EPSILON
  ) {
    return true;
  }

  return false;
}

// check that a segment is overlapping at least one other segment in a group
function segmentOverlapsGroupBounds(
  group: DimensionTreeSegmentGroup,
  segment: DimensionTreeSegment,
  segmentStartOffset = 0,
  segmentEndOffset = 0,
): boolean {
  let minT = Number.MAX_VALUE;
  let maxT = -Number.MAX_VALUE;

  for (const oSegment of group.segments) {
    if (oSegment.eptCS.tangentT < minT) {
      minT = oSegment.eptCS.tangentT;
    }
    if (oSegment.eptCS.tangentT > maxT) {
      maxT = oSegment.eptCS.tangentT;
    }
    if (oSegment.sptCS.tangentT < minT) {
      minT = oSegment.sptCS.tangentT;
    }
    if (oSegment.sptCS.tangentT > maxT) {
      maxT = oSegment.sptCS.tangentT;
    }
  }

  const segmentStartT = segment.sptCS.tangentT - segmentStartOffset;
  const segmentEndT = segment.eptCS.tangentT + segmentEndOffset;

  const overlap = (segmentStartT >= minT && segmentStartT <= maxT) || (segmentEndT >= minT && segmentEndT <= maxT);

  return overlap;
}

function makeDimensionTreeBounds(input: { [K in DimensionRowLevel]: PolyLine2[] }) {
  let aabbInner = aabb2ByPolyLine2s(input[DimensionRowLevel.ROW_2]);

  let aabbOuter = aabb2ByPolyLine2s([
    ...input[DimensionRowLevel.BOUNDS],
    ...input[DimensionRowLevel.ROW_2],
    ...input[DimensionRowLevel.ROW_3],
    ...input[DimensionRowLevel.ROW_4],
  ]);

  // handle edge cases with missing polylines
  if (!aabbInner && !aabbOuter) {
    // let this fail - there are no segments to dimension
  } else if (aabbInner && !aabbOuter) {
    aabbOuter = aabbInner;
  } else if (aabbOuter && !aabbInner) {
    aabbInner = aabbOuter;
  }

  return {
    aabbOuter,
    aabbInner,
  };
}

function makeDimensionTreeBoundSegments({
  aabbInner,
  aabbOuter,
}: {
  aabbInner: AABB2 | null;
  aabbOuter: AABB2 | null;
}) {
  const segments: PolyLine2[] = [];

  if (!aabbInner || !aabbOuter) {
    return segments;
  }

  const bottomLine = polyLine2ByTwoPoints(aabbInner.minX, aabbOuter.minY, aabbInner.maxX, aabbOuter.minY);
  segments.push(bottomLine);

  const rightLine = polyLine2ByTwoPoints(aabbOuter.maxX, aabbInner.minY, aabbOuter.maxX, aabbInner.maxY);
  segments.push(rightLine);

  const topLine = polyLine2ByTwoPoints(aabbInner.maxX, aabbOuter.maxY, aabbInner.minX, aabbOuter.maxY);
  segments.push(topLine);

  const leftLine = polyLine2ByTwoPoints(aabbOuter.minX, aabbInner.maxY, aabbOuter.minX, aabbInner.minY);
  segments.push(leftLine);

  return segments;
}

interface ProjectedSegment {
  readonly segment: PolyLine2;
  readonly sptDist: number;
  readonly eptDist: number;
}

function projectLineToAxis(line: PolyLine2) {
  const projectedLines: ProjectedSegment[] = [];

  const [normalX, normalY] = line2Normal(line[0], line[1], line[2], line[3], false);
  const aabb = polyLine2AABB2(line);
  const rect = rect2ByAABB2(aabb);

  if (normalX >= 0) {
    // right
    const segment = polyLine2Edge(rect, 1);
    const sptDist = vec2Dist(line[0], line[1], segment[0], segment[1]);
    const eptDist = vec2Dist(line[2], line[3], segment[2], segment[3]);

    projectedLines.push({ segment, sptDist, eptDist });
  } else {
    // left
    const segment = polyLine2Edge(rect, 3);
    const sptDist = vec2Dist(line[0], line[1], segment[0], segment[1]);
    const eptDist = vec2Dist(line[2], line[3], segment[2], segment[3]);

    projectedLines.push({ segment, sptDist, eptDist });
  }

  if (normalY >= 0) {
    // top
    const segment = polyLine2Edge(rect, 2);
    const sptDist = vec2Dist(line[0], line[1], segment[0], segment[1]);
    const eptDist = vec2Dist(line[2], line[3], segment[2], segment[3]);

    projectedLines.push({ segment, sptDist, eptDist });
  } else {
    // bottom

    const segment = polyLine2Edge(rect, 0);
    const sptDist = vec2Dist(line[0], line[1], segment[0], segment[1]);
    const eptDist = vec2Dist(line[2], line[3], segment[2], segment[3]);

    projectedLines.push({ segment, sptDist, eptDist });
  }

  return projectedLines;
}

function vec2ToCoordinateSystem(x: number, y: number, angle: number): DimensionTreeCoordinateSystem {
  const normalX = Math.sin(angle);
  const normalY = Math.cos(angle);

  const normalT = vec2Dot(x, y, normalX, normalY);

  // This could also just be a `vec2Dot` however it's a load bearing numeric imprecision.
  // If you use `vec2Dot` expect baseline changes.
  const [tangentX, tangentY] = vec2Rotate(normalX, normalY, -HALF_PI);
  const tangentT = closestParamLine2(0, 0, tangentX, tangentY, x, y, false);

  return {
    normalX,
    normalY,
    normalT, // distance on the new y axis
    tangentX,
    tangentY,
    tangentT, // distance on the new x axis
  };
}

function findClosestAngle(tAngle: number, map: { [angle: number]: any }, threshold = ANGLE_EPSILON) {
  const rAngle = tAngle % TWO_PI;
  for (const angle in map) {
    if (Math.abs(+angle - rAngle) < threshold) {
      return +angle;
    }
  }
  return null;
}

function anglesAreFacing(a1: number, a2: number) {
  // return Math.abs(a1 - a2) > HALF_PI;
  const max = Math.max(a1, a2);
  const min = Math.min(a1, a2);
  const diff = max - min;
  return diff > TWO_PI / 4 && diff < (TWO_PI * 3) / 4;
}

function line2Angle(ax: number, ay: number, bx: number, by: number) {
  // This could just be `let angle = Math.atan2(by - ay, ax - bx);`
  // however it's a load bearing numeric imprecision.
  // If you use `Math.atan2` expect baseline changes.
  const [normalX, normalY] = line2Normal(ax, ay, bx, by, false);
  let angle = vec2AngleBetween(normalX, normalY, 0, 1);

  angle = angle < 0 ? angle + TWO_PI : angle;
  angle = Math.abs(angle - TWO_PI) < ANGLE_EPSILON ? 0 : angle;

  return angle;
}

function removeDims(
  evaluator: (ax, ay, bx, by, cx, cy, dx, dy) => boolean,
  targetDims: Dimension[],
  sourceDims?: Dimension[],
): Dimension[] {
  const ret: Dimension[] = [];

  const dimSource = sourceDims || targetDims;
  const sourceDimInfo = dimSource.map((dim) => getOrientedDimLine(dim.dimLine));
  const targetDimInfo = sourceDims ? targetDims.map((dim) => getOrientedDimLine(dim.dimLine)) : sourceDimInfo;

  outerloop: for (let i = 0; i < targetDims.length; i++) {
    const [ax, ay, bx, by] = targetDimInfo[i];
    for (let j = 0; j < (!sourceDims ? i - 1 : dimSource.length); j++) {
      const [cx, cy, dx, dy] = sourceDimInfo[j];

      if (evaluator(ax, ay, bx, by, cx, cy, dx, dy)) {
        continue outerloop;
      }
    }
    ret.push(targetDims[i]);
  }

  return ret;
}

// remove duplicate dimensions from a target list that exist in the source list
// duplicate dimensions in this case are any dimension that dimensions the same targents at an angle
export function removeDuplicateDims(targetDims: Dimension[], sourceDims?: Dimension[]): Dimension[] {
  return removeDims(
    (ax, ay, bx, by, cx, cy, dx, dy) => {
      const dirx = bx - ax;
      const diry = by - ay;
      // if the lines are not parallel, they cannot be duplicates
      if (Math.abs(vec2Dot(dirx, diry, dy - cy, cx - dx)) > DIST_EPSILON) {
        return false;
      }

      if (vec2ArePerp(dirx, diry, ax - cx, ay - cy) && vec2ArePerp(dirx, diry, bx - dx, by - dy)) {
        return true; // a, b, c, d form a rectangle, dim is a duplicate, continue the outer loop
      }

      if (vec2ArePerp(dirx, diry, bx - cx, by - cy) && vec2ArePerp(dirx, diry, ax - dx, ay - dy)) {
        return true; // a, b, c, d form a rectangle, dim is a duplicate, continue the outer loop
      }

      return false;
    },
    targetDims,
    sourceDims,
  );
}

export function removeCoincidentDims(targetDims: Dimension[], sourceDims?: Dimension[]): Dimension[] {
  return removeDims(
    (ax, ay, bx, by, cx, cy, dx, dy) => {
      return line2IsCoincident(ax, ay, bx, by, cx, cy, dx, dy);
    },
    targetDims,
    sourceDims,
  );
}
